import type { FC } from 'react';
import { useMemo } from 'react';

import {
  Modal,
  ModalContent,
  ModalOverlay,
  ModalTitle,
} from '@mit/ui-components/right-tower';

import * as style from './PackModal.css';
import { PURCHASE_BUTTON_IMAGE_URL } from '../../constants';
import { formatNumber, FormattedMessage } from '../../utils';
import { AvailableLabel } from '../AvailableLabel';
import { CardButtons } from '../CardButtons';

import type { Bundle } from '@site-builder/store-sdk';

interface PackModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly packData?: Bundle | null;
}

export const PackModal: FC<PackModalProps> = ({
  isOpen,
  onClose,
  packData,
}) => {
  const availabilityInfo = useMemo(() => {
    const perUser = packData?.limits?.perUser;
    return {
      available: perUser?.available ?? perUser?.total ?? 1,
      total: perUser?.total ?? 1,
    };
  }, [packData]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Modal open={isOpen} onOpenChange={handleOpenChange}>
      <ModalOverlay className={style.modalOverlay}>
        <ModalContent
          className={`${style.modalContent} msm-frame msm-frame--brown`}
        >
          {packData ? (
            <>
              <button
                className={style.modalClose}
                onClick={onClose}
                type='button'
                aria-label='Close modal'
              >
                <img
                  src={PURCHASE_BUTTON_IMAGE_URL}
                  alt='Purchase button'
                  className={style.modalButtonImage}
                />
              </button>

              <div className={style.modalHeader}>
                {packData.imageUrl && (
                  <div className={style.modalHeroImageWrapper}>
                    <img
                      src={packData.imageUrl}
                      alt={packData.name}
                      className={style.modalHeroImage}
                    />
                  </div>
                )}
                <div className={style.modalHeaderText}>
                  <ModalTitle className={style.modalTitle}>
                    {packData.name}
                  </ModalTitle>
                </div>
              </div>

              <div className={style.modalBody}>
                <h3 className={style.modalBonusesTitle}>
                  <FormattedMessage id='packs.modal-title' />
                </h3>
                <div className={style.modalBonuses}>
                  {packData.type === 'bundle' &&
                    'content' in packData &&
                    packData.content &&
                    packData.content.map(
                      (
                        contentItem: {
                          originalItem: {
                            sku: string;
                            name: string;
                            imageUrl?: string;
                          };
                          quantity: number;
                        },
                        index: number,
                      ) => (
                        <div
                          key={contentItem.originalItem.sku || index}
                          className={style.modalBonusItem}
                        >
                          {contentItem.originalItem.imageUrl && (
                            <img
                              src={contentItem.originalItem.imageUrl}
                              alt={contentItem.originalItem.name}
                              className={style.modalBonusIcon}
                            />
                          )}
                          <div className={style.modalBonusText}>
                            <p className={style.modalBonusName}>
                              {contentItem.originalItem.name}
                            </p>
                            <p className={style.modalBonusAmount}>
                              {formatNumber(contentItem.quantity)}
                            </p>
                          </div>
                        </div>
                      ),
                    )}
                </div>

                <div className={style.modalFooter}>
                  <CardButtons />
                  <AvailableLabel
                    available={availabilityInfo.available}
                    total={availabilityInfo.total}
                  />
                </div>
              </div>
            </>
          ) : null}
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};
